import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import config from '../config';
import { useAdminApi } from './hooks/useAdminApi';
import { AdminBreadcrumbs } from './components/AdminUiPrimitives';
import { AdminPageHeader } from './components/AdminMasterDetail';

const API_BASE_URL = `${config.apiBaseUrl}/api`;

interface FilterOption { id: number; name: string; }

interface StudentSessionRow {
  sessionId: number;
  studentUserId: number | null;
  studentName: string;
  teacherUserId: number;
  teacherName: string;
  classroomId: number | null;
  classroomName: string;
  scheduleTitle: string;
  startDateTime: string;
  endDateTime: string;
  durationHours: number;
  status: string;
}

interface ReportData {
  from: string;
  to: string;
  totalSessions: number;
  totalHours: number;
  rows: StudentSessionRow[];
  teacherOptions: FilterOption[];
  studentOptions: FilterOption[];
  classroomOptions: FilterOption[];
}

const toLocalDateInput = (d: Date) => d.toISOString().split('T')[0];

const defaultFrom = () => {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return toLocalDateInput(d);
};

const statusColor = (s: string) => {
  if (s === 'Completed') return '#16a34a';
  if (s === 'Cancelled') return '#dc2626';
  if (s === 'InProgress') return '#d97706';
  return '#2563eb';
};

const exportCsv = (rows: StudentSessionRow[]) => {
  const header = 'Student,Teacher,Classroom,Schedule,Date,Duration (hrs),Status';
  const lines = rows.map(r => {
    const date = new Date(r.startDateTime).toLocaleDateString();
    return `"${r.studentName}","${r.teacherName}","${r.classroomName}","${r.scheduleTitle}","${date}",${r.durationHours},"${r.status}"`;
  });
  const blob = new Blob([[header, ...lines].join('\n')], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'student-sessions-report.csv';
  a.click();
  URL.revokeObjectURL(url);
};

const ReportStudentSessions: React.FC = () => {
  const { getManagementToken, getOrganizationHeaders, hasActiveOrganization } = useAdminApi();

  const [fromDate, setFromDate] = useState(defaultFrom());
  const [toDate, setToDate] = useState(toLocalDateInput(new Date()));
  const [studentFilter, setStudentFilter] = useState('');
  const [teacherFilter, setTeacherFilter] = useState('');
  const [classroomFilter, setClassroomFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<ReportData | null>(null);

  const load = useCallback(async () => {
    if (!hasActiveOrganization) return;
    setLoading(true);
    setError(null);
    try {
      const token = await getManagementToken();
      const headers = getOrganizationHeaders(token);
      const params = new URLSearchParams();
      if (fromDate) params.set('from', fromDate);
      if (toDate) params.set('to', toDate + 'T23:59:59');
      if (studentFilter) params.set('studentUserId', studentFilter);
      if (teacherFilter) params.set('teacherUserId', teacherFilter);
      if (classroomFilter) params.set('classroomId', classroomFilter);
      if (statusFilter) params.set('status', statusFilter);
      const res = await axios.get<ReportData>(`${API_BASE_URL}/reports/student-sessions?${params}`, { headers });
      setData(res.data);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to load report.');
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, studentFilter, teacherFilter, classroomFilter, statusFilter, getManagementToken, getOrganizationHeaders, hasActiveOrganization]);

  useEffect(() => { void load(); }, [load]);

  const summaryCards = [
    { label: 'Total Sessions', value: data?.totalSessions ?? '—' },
    { label: 'Total Hours', value: data ? `${data.totalHours} hrs` : '—' },
    { label: 'Students', value: data ? new Set(data.rows.map(r => r.studentUserId)).size : '—' },
  ];

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <AdminBreadcrumbs items={[
        { label: 'Administration', to: '/analytics' },
        { label: 'Reports', to: '/reports/student-sessions' },
        { label: 'Student Sessions', isCurrent: true },
      ]} />

      <AdminPageHeader
        title="Student Sessions"
        subtitle="Session attendance per student"
        actions={[
          { key: 'export', label: 'Export CSV', variant: 'secondary', onClick: () => data && exportCsv(data.rows) },
          { key: 'refresh', label: 'Refresh', variant: 'secondary', onClick: () => void load() },
        ]}
      />

      {/* Filters */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, background: '#f8fbff', border: '1px solid #dfe8f8', borderRadius: 10, padding: '12px 14px', alignItems: 'flex-end' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: '#475569' }}>From</label>
          <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
            style={{ border: '1px solid #cbd5e1', borderRadius: 6, padding: '6px 10px', fontSize: 13 }} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: '#475569' }}>To</label>
          <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
            style={{ border: '1px solid #cbd5e1', borderRadius: 6, padding: '6px 10px', fontSize: 13 }} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: '#475569' }}>Student</label>
          <select value={studentFilter} onChange={e => setStudentFilter(e.target.value)}
            style={{ border: '1px solid #cbd5e1', borderRadius: 6, padding: '6px 10px', fontSize: 13, minWidth: 160 }}>
            <option value="">All Students</option>
            {data?.studentOptions.map(s => <option key={s.id} value={String(s.id)}>{s.name}</option>)}
          </select>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: '#475569' }}>Teacher</label>
          <select value={teacherFilter} onChange={e => setTeacherFilter(e.target.value)}
            style={{ border: '1px solid #cbd5e1', borderRadius: 6, padding: '6px 10px', fontSize: 13, minWidth: 160 }}>
            <option value="">All Teachers</option>
            {data?.teacherOptions.map(t => <option key={t.id} value={String(t.id)}>{t.name}</option>)}
          </select>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: '#475569' }}>Classroom</label>
          <select value={classroomFilter} onChange={e => setClassroomFilter(e.target.value)}
            style={{ border: '1px solid #cbd5e1', borderRadius: 6, padding: '6px 10px', fontSize: 13, minWidth: 150 }}>
            <option value="">All Classrooms</option>
            {data?.classroomOptions.map(c => <option key={c.id} value={String(c.id)}>{c.name}</option>)}
          </select>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: '#475569' }}>Status</label>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            style={{ border: '1px solid #cbd5e1', borderRadius: 6, padding: '6px 10px', fontSize: 13, minWidth: 130 }}>
            <option value="">All Statuses</option>
            <option value="Scheduled">Scheduled</option>
            <option value="InProgress">In Progress</option>
            <option value="Completed">Completed</option>
            <option value="Cancelled">Cancelled</option>
          </select>
        </div>
      </div>

      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
        {summaryCards.map(c => (
          <div key={c.label} style={{ background: '#fff', border: '1px solid #dfe8f8', borderRadius: 10, padding: '14px 16px' }}>
            <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600, marginBottom: 4 }}>{c.label}</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: '#1e3a8a' }}>{c.value}</div>
          </div>
        ))}
      </div>

      {/* Table */}
      <div style={{ border: '1px solid #dfe8f8', borderRadius: 12, background: '#fff', overflow: 'hidden' }}>
        <div style={{ padding: '10px 14px', borderBottom: '1px solid #edf2fb', fontWeight: 700, color: '#173159', fontSize: 14 }}>
          Results {data ? `(${data.rows.length} sessions)` : ''}
        </div>

        {!hasActiveOrganization ? (
          <div style={{ padding: 20, color: '#92400e' }}>No organisation selected.</div>
        ) : error ? (
          <div style={{ padding: 20, color: '#b91c1c' }}>{error}</div>
        ) : loading ? (
          <div style={{ padding: 20, color: '#475569' }}>Loading...</div>
        ) : !data || data.rows.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>No sessions found for selected filters.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f8fbff', color: '#334155' }}>
                  {['Student', 'Teacher', 'Classroom', 'Schedule', 'Date', 'Time', 'Duration', 'Status'].map(h => (
                    <th key={h} style={{ textAlign: 'left', padding: '8px 12px', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.rows.map(row => {
                  const start = new Date(row.startDateTime);
                  const end = new Date(row.endDateTime);
                  return (
                    <tr key={row.sessionId} style={{ borderBottom: '1px solid #edf2fb' }}>
                      <td style={{ padding: '8px 12px', fontWeight: 600, color: '#1e3a8a' }}>{row.studentName}</td>
                      <td style={{ padding: '8px 12px' }}>{row.teacherName}</td>
                      <td style={{ padding: '8px 12px' }}>{row.classroomName}</td>
                      <td style={{ padding: '8px 12px', color: '#475569' }}>{row.scheduleTitle}</td>
                      <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>{start.toLocaleDateString()}</td>
                      <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>
                        {start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} – {end.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </td>
                      <td style={{ padding: '8px 12px' }}>{row.durationHours} hrs</td>
                      <td style={{ padding: '8px 12px', fontWeight: 600, color: statusColor(row.status) }}>{row.status}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportStudentSessions;
